import { useState, useEffect } from 'react';
import styled, { css } from 'styled-components';

const labelStyles = css`
  --input-border-color: var(--grey);

  padding: 2.4rem 1.6rem 0.8rem;

  & + label {
    --label-color: var(--charcoal-2);
    font-size: 1.2rem;
    line-height: 2rem;
    letter-spacing: 0.5px;
    top: 0.4rem;
  }
`;

export const StyledTextInputWrapper = styled.div`
  position: relative;
  height: min-content;

  label {
    --label-color: var(--charcoal);

    color: var(--label-color);
    position: absolute;
    pointer-events: none;
    font-size: 1.6rem;
    line-height: 2.4rem;
    inset: 1.6rem;

    transition: all 0.15s ease;
    cursor: text;
  }

  input {
    --input-border-color: var(--grey);
    --background-color: var(--white);
    --input-color: var(--charcoal);

    appearance: none;
    background: var(--background-color);
    border: 1px solid var(--input-border-color);
    font-size: 1.6rem;
    line-height: 2.4rem;
    padding: 1.6rem;
    width: 100%;
    outline: none;

    ${(props) =>
      (props.hasValue || props.isInvalid) &&
      css`
        ${labelStyles}

        &:invalid,
        &.error {
          --input-border-color: var(--bdo-red) !important;
        }
      `}

    &:focus {
      --input-border-color: var(--charcoal-2);
    }

    &:-webkit-autofill {
      ${labelStyles}
    }

    &:autofill {
      ${labelStyles}
    }

    &:disabled,
    &:read-only {
      --background-color: var(--light-grey);
      --color: var(--grey);
    }
  }
`;

const ErrorMessage = styled.span`
  background: var(--white);
  bottom: -100%;
  box-shadow: 0 3px 5px rgb(91 110 127 / 15%);
  color: var(--bdo-red);
  display: flex;
  font-size: 1.4rem;
  gap: 1rem;
  left: 0;
  line-height: 2.4rem;
  margin-top: 0.8rem;
  padding: 0.5rem 1rem;
  width: fit-content;
  z-index: 1;
`;

/**
 * @typedef {import('react').DetailedHTMLProps<import('react').InputHTMLAttributes, HTMLInputElement>} InputParams
 * @param {{
 *   label: string;
 *   id: string;
 *   errorMessages: {
 *     -readonly [key in keyof ValidityState]?: string
 *   };
 * } & InputParams}
 */
const TextInput = ({ label, id, errorMessages, onInput, ...inputProps }) => {
  const [hasValue, setHasValue] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const { additionalAriaLabel, className, ...props } = inputProps;

  const handleInput = (e) => {
    setErrorMessage('');
    setHasValue(!!e.target.value);
    onInput?.(e);
  };

  /** @type {React.FormEventHandler<HTMLInputElement>} */
  const checkInvalid = (e) => {
    e?.preventDefault();

    const { validity } = e.currentTarget;

    if (validity.valueMissing) {
      setErrorMessage(errorMessages?.valueMissing ?? 'Please enter a value');
    } else if (validity.patternMismatch && errorMessages?.patternMismatch) {
      setErrorMessage(errorMessages.patternMismatch);
    } else if (validity.typeMismatch && errorMessages?.typeMismatch) {
      setErrorMessage(errorMessages.typeMismatch);
    } else if (errorMessages?.custom?.length > 0) {
      setErrorMessage(errorMessages.custom);
    } else {
      setErrorMessage('');
    }
  };

  useEffect(() => {
    if (props.value && !hasValue) {
      setHasValue(true);
    }
    if (errorMessages?.custom === '' && hasValue) {
      setErrorMessage('');
    }
  }, [inputProps]);

  useEffect(() => {
    if (errorMessages?.custom?.length > 0) {
      setErrorMessage(errorMessages.custom);
    } else {
      setErrorMessage('');
    }
  }, [errorMessages]);

  return (
    <StyledTextInputWrapper hasValue={hasValue} isInvalid={!!errorMessage}>
      <input
        disabled={props.disabled}
        id={id}
        onInput={handleInput}
        onInvalid={checkInvalid}
        {...props}
        onChange={checkInvalid}
        onBlur={checkInvalid}
        className={`${inputProps.className || ''} ${errorMessage ? 'error' : ''}`}
        aria-labelledby={`${id}-label ${id}-error ${inputProps?.additionalAriaLabel || ''}`}
      />
      <label htmlFor={id} id={`${id}-label`}>
        {label}
      </label>
      {errorMessage && (
        <ErrorMessage id={`${id}-error`}>
          <span className="material-icons-outlined">warning</span>
          {errorMessage}
        </ErrorMessage>
      )}
    </StyledTextInputWrapper>
  );
};

export default TextInput;
